import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import ErrorPage403 from '@/app/routes/errorPage403';
import { Button } from '@/shared/components/Buttons';
import { ButtonVariant } from '@/shared/components/Buttons/types';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Snackbar } from '@shared/components/Snackbar';
import InlineLayout from '@/shared/layout/inlineLayout';
import UpdateStatusDialog from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { useViewQmsCategory } from './useViewQmsCategory';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import ViewQmsCategoryContent from './ViewQmsCategoryContent';
import ViewQmsCategoryErrorScreen from './ViewQmsCategoryErrorScreen';
import { QMS_CATEGORY_LABELS } from '../../constants/labels';
import { QMS_CATEGORY_LABELS as TL } from '../../constants/translations/labels';
import { useUpdateQmsCategoryStatus } from '../../hooks/useUpdateQmsCategoryStatus';
import { getQmsCategoryStatusDialogConfig } from '../categoryList/qmsCategoryStatusDialogConfig';

const EDIT_PATH_PREFIX = '/Kategorie/Edycja';

const NAV_ITEMS = [
  {
    id: 'PodstawoweInformacje',
    label: QMS_CATEGORY_LABELS.TAB_BASIC_INFO,
    dataTestId: 'tab-basic-info',
  },
  {
    id: 'QMSDocuments',
    label: TL.TAB_QMS_DOCUMENTS.polish,
    dataTestId: 'tab-qms-documents',
  },
] as const;

const ViewQmsCategory: React.FC<{ categoryId: string }> = ({ categoryId }) => {
  const navigate = useNavigate();
  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();

  const {
    category,
    isLoading,
    showErrorScreen,
    notFound,
    accessError,
    statusLabel,
    formattedDescription,
    formattedCreatedAt,
    formattedUpdatedAt,
    refetch,
  } = useViewQmsCategory(categoryId);

  const {
    selectedCategory,
    isDialogOpen,
    isSubmitting: isStatusSubmitting,
    handleOpenDialog,
    handleDialogClose,
    handleConfirm,
    showSuccessSnackbar,
    successMessage,
    setShowSuccessSnackbar,
    showErrorSnackbar,
    errorMessage: statusErrorMessage,
    setShowErrorSnackbar,
  } = useUpdateQmsCategoryStatus({ onSuccess: refetch });

  if (!isPrivilegesLoading && !privileges.qmsDocCategoryView) {
    return <ErrorPage403 />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  const titleAction = (
    <div className="inline-flex items-center gap-2" data-testid="status-toggle">
      <span className="text-bodyMedium text-on-surface-variant whitespace-nowrap">
        {QMS_CATEGORY_LABELS.STATUS_LABEL}
      </span>
      <div
        className="inline-flex cursor-pointer"
        onClick={() => {
          if (category) {
            handleOpenDialog({
              id: categoryId,
              name: category.name,
              status: category.status,
            });
          }
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div style={{ pointerEvents: 'none' }}>
          <ToggleButton
            checked={category?.status === 'active'}
            onChange={() => {}}
            label={statusLabel}
            size="Small"
          />
        </div>
      </div>
    </div>
  );

  const categoryHeader = (
    <div className="flex items-center justify-between w-full gap-4">
      <h2
        className="min-w-0 break-all font-medium text-neutral-10"
        style={{ fontSize: '18px', lineHeight: '28px' }}
        data-testid="category-name"
      >
        {category?.name ?? '—'}
      </h2>
      <Button
        label={QMS_CATEGORY_LABELS.BUTTON_EDIT}
        variant={ButtonVariant.Primary}
        size="Medium"
        onClick={() => navigate(`${EDIT_PATH_PREFIX}/${categoryId}`)}
        className="!py-[10px] !px-[16px] !font-medium !text-[14px] hover:cursor-pointer"
        style={{ borderRadius: '8px', height: '40px' }}
      />
    </div>
  );

  return (
    <React.Fragment key={categoryId}>
      {showErrorScreen ? (
        <ViewQmsCategoryErrorScreen />
      ) : (
        <InlineLayout
          title={QMS_CATEGORY_LABELS.VIEW_TITLE}
          titleAction={titleAction}
          navItems={[...NAV_ITEMS]}
          header={categoryHeader}
          searchParamKey="tab"
          defaultActiveId="PodstawoweInformacje"
          contentByNavId={{
            PodstawoweInformacje: (
              <ViewQmsCategoryContent
                category={category}
                formattedDescription={formattedDescription}
                formattedCreatedAt={formattedCreatedAt}
                formattedUpdatedAt={formattedUpdatedAt}
              />
            ),
            QMSDocuments: (
              <div className="border border-[#DCDDE0] rounded-lg flex-1 items-center justify-center py-16">
                <p
                  className="text-sm text-on-surface-variant text-center"
                  data-testid="qms-documents-empty"
                >
                  {TL.QMS_DOCUMENTS_EMPTY.polish}
                </p>
              </div>
            ),
          }}
        />
      )}

      {selectedCategory && (
        <UpdateStatusDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleConfirm}
          isSubmitting={isStatusSubmitting}
          {...getQmsCategoryStatusDialogConfig(
            selectedCategory.name,
            selectedCategory.status === 'active'
          )}
        />
      )}

      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        onClose={() => setShowSuccessSnackbar(false)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton={true}
      />

      <Snackbar
        open={showErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={() => setShowErrorSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </React.Fragment>
  );
};

export default ViewQmsCategory;
